
import Vue from 'vue';
import {
  DocumentSummaries, DocumentSummary, IngestBox, Label, Summary, SummarySlice, Time, User,
} from '@/model';
import api from '@/rest-api';
import labels from '@/store/labels';
import users from '@/store/users';
import {
  distinct, indexed, sortBy, notEmpty,
} from '@/util/array-util';
import Progress from '@/components/Progress.vue';
import ExpansionPanel from '@/components/ExpansionPanel.vue';
import LabelPanel from '@/components/LabelPanel.vue';
import IconButton from '@/components/IconButton.vue';
import LabeledTextWithContext from '@/components/LabeledTextWithContext.vue';
import { notify, notifyError } from '@/store/notification';
import { GroupedInterval, groups } from '@/util/text-selection';
import authModule from '@/store/auth-module';
import TextEditor from '@/components/TextEditor.vue';
import { diffText } from '@/util/diff';
import { isShortSummaryGroup, getTotalSliceChars } from '@/util/summary-util';

type Group = DocumentSummaries['groups'][number];

interface TextSource {
  boxId: number;
  pageNo: number;
  summarized: GroupedInterval<SummarySlice>[];
  unsummarized: GroupedInterval<SummarySlice>[];
}

const defualtPrompt = 'Cross-Summary Instruction \n\n'
+ 'The summaries are intended to be read together as a complete analytical product. To avoid redundancy: \n\n'
+ 'Each summary must contain only theme-specific content. \n'
+ 'Do not repeat the same information across summaries, even if the underlying input is shared. \n'
+ 'When a detail appears relevant to more than one theme, include it only in the theme where it is most directly relevant. \n\n';

export default Vue.extend({
  name: 'DocumentSummary',
  components: {
    Progress,
    LabelPanel,
    IconButton,
    LabeledTextWithContext,
    ExpansionPanel,
    TextEditor,
  },

  props: {
    documentId: { type: Number, required: true },
    group: { type: Object as () => Group, required: true },
    relatedSummaries: { type: Object as () => DocumentSummaries },
  },

  data() {
    return {
      opened: undefined as undefined | number,
      generating: false,
      expansionPanels: [] as number[],
      additionalInstructions: undefined as undefined | string,
      includeRelated: [] as boolean[],
      contextInstructions: defualtPrompt as undefined | string,
    };
  },

  computed: {
    current(): DocumentSummary | undefined {
      if (this.opened) {
        const found = this.summaries.find((ds) => ds.id === this.opened);
        if (found) {
          return found;
        } else if (this.group.head?.id === this.opened) {
          return this.group.head;
        } else {
          return undefined;
        }
      } else {
        return this.group.head;
      }
    },
    currentIsPersisted(): boolean {
      return !!this.current?.id;
    },
    summaries(): DocumentSummary[] {
      const head = this.group.head;
      const tail = this.group.tail;
      return sortBy<DocumentSummary>((ds) => ds.summary.edited ?? ds.summary.created)(
        [
          ...(head ? [head] : []),
          ...(tail ?? []),
        ],
      ).reverse();
    },
    hasUnsummarizedSlices(): boolean {
      return !!this.slices.find((s) => s.unsummarized.length > 0);
    },
    isShortSlices(): boolean {
      return isShortSummaryGroup(this.group);
    },
    totalSliceChars(): number {
      return getTotalSliceChars(this.group);
    },
    slices(): TextSource[] {
      const slices = [
        ...(this.current?.summary.slices ?? []),
        ...(this.group.specSlices ?? []),
      ];
      slices.sort((a, b) => {
        const p = a.pageNo - b.pageNo;
        return p !== 0 ? p : a.seqNo - b.seqNo;
      });
      const boxes = indexed(slices, (s) => s.boxId);
      return boxes.indexKeys().map((boxId) => {
        const ss = boxes.get(boxId) ?? [];
        const gs = groups(ss);
        const intervals = (summarized: boolean) => gs
          .filter((g) => summarized === !!g.group.find((s) => s.id !== 0))
          .map((g) => ({
            ...g,
            group: g.group.filter((s) => summarized === !!s.id),
          }))
          .filter((g) => g.group.length > 0);
        return {
          boxId,
          summarized: intervals(true),
          unsummarized: intervals(false),
          pageNo: ss[0]?.pageNo,
        };
      });
    },
    async sources(): Promise<Array<TextSource & { box: IngestBox }>> {
      const slices = this.slices;
      const boxIds = distinct(slices.map((s) => s.boxId));
      const boxes = await api.ingestBoxes(boxIds);
      return boxIds.map((boxId) => {
        const sliceGroup = slices.find((s) => s.boxId === boxId);
        const box = boxes.find((b) => b.id === boxId);
        if (!sliceGroup || !box) {
          throw new Error();
        }
        return {
          ...sliceGroup,
          box,
        };
      });
    },
  },

  watch: {
    current: {
      handler(change: DocumentSummary | undefined, prev: DocumentSummary | undefined) {
        if (change && change.summary.id !== prev?.summary.id) {
          if (change.summary.motivation && change.summary.motivation.length > 0) {
            this.expansionPanels.push(0);
          }
        }
      },
      immediate: true,
    },
    relatedSummaries: {
      immediate: true,
      handler(newValue) {
        this.includeRelated = newValue?.groups.map((_) => true) ?? [];
      },
    },
  },

  methods: {
    label(id: number): Label | undefined {
      return labels.getters.lookup.get(id);
    },
    user(userId: number): User | undefined {
      return users.getters.lookup.get(userId);
    },
    time(t: Time) {
      return Time.asString(t);
    },
    open(documentSummaryId: number) {
      this.opened = documentSummaryId;
    },
    summaryType(summary: Summary): 'authored' | 'generated' | 'corrected' {
      if (summary.externalRequestId) {
        return summary.correction ? 'corrected' : 'generated';
      } else {
        return 'authored';
      }
    },
    showDocument(interval: GroupedInterval<SummarySlice>) {
      const slice = interval.group[0];
      this.$emit('showDocument', { documentId: slice.documentId, ingestId: slice.ingestId, fragmentId: slice.fragmentIds[0] });
    },
    selfApproval(ds: DocumentSummary): boolean | undefined {
      const self = authModule.state.session?.userId;
      const approval = ds.approvals.find((a) => a.userId === self);
      return approval !== undefined ? approval.approved : undefined;
    },
    anyApproved(ds: DocumentSummary): boolean {
      return ds.approvals.some((a) => a.approved);
    },
    async saveEditSummary(id: number, text: string) {
      try {
        const edited = await api.editSummary(id, text);
        this.update((ds) => ds.summary.id === edited.id, 'summary', edited);
        notify('Summary saved');
      } catch {
        notifyError('Could not save summary');
      }
    },
    async saveMotivationSummary(id: number, motivation: string) {
      try {
        const edited = await api.editMotivation(id, motivation);
        this.update((ds) => ds.summary.id === edited.id, 'summary', edited);
        notify('Motivation saved');
      } catch {
        notifyError('Could not save motivation');
      }
    },
    async promote(ds: DocumentSummary) {
      try {
        const edited = await api.promoteDocumentSummary(ds.id, true);
        this.update((stale) => stale.id === ds.id, 'head', edited.head);
        this.update((stale) => stale.id !== ds.id, 'head', !edited.head);
        // update group, with Vue.set
        /* eslint-disable no-undef */
        const group = this.group as Writeable<Group>;

        if (edited.head) {
          const oldHead = group.head;
          Vue.set(group, 'head', edited);
          // update tail
          const tail = group.tail?.filter((s) => s.id !== edited.id) ?? [];
          Vue.set(group, 'tail', [...tail, oldHead]);
        }

        notify('Summary promoted');
      } catch {
        notifyError('Could not promote summary');
      }
    },
    async approve(ds: DocumentSummary, approve: boolean) {
      try {
        const approved = ds.approvals.find((a) => a.userId === authModule.state.session?.userId);
        let edited;
        if (approved && approved.approved === approve) {
          // confirm before undoing approval
          // eslint-disable-next-line no-alert
          if (window.confirm(`Are you sure you want to undo your ${approve ? 'approval' : 'disapproval'} of this summary?`)) {
            edited = await api.undoApproveDocumentSummary(ds.id);
          }
        } else {
          edited = await api.approveDocumentSummary(ds.id, approve);
        }
        this.update((stale) => ds.id === stale.id, 'approvals', edited.approvals);
        notify(`Summary ${approve ? 'approved' : 'disapproved'}`);
        // if current head is not approved and approved is approved, promote
        const currentHead = this.group?.head;
        if (currentHead && currentHead.id !== ds.id && !this.anyApproved(currentHead) && this.anyApproved(edited)) {
          await this.promote(ds);
        }
      } catch {
        notifyError(`Could not ${approve ? 'approve' : 'disapprove'} summary`);
      }
    },

    async revertToOriginal(id: number) {
      try {
        if (window.confirm('Are you sure you want to revert to the generated summary?')) {
          const edited = await api.deleteEditSummary(id);
          this.update((ds) => ds.summary.id === edited.id, 'summary', edited);
        }
      } catch {
        notifyError('Could not revert summary');
      }
    },

    update<K extends keyof DocumentSummary>(p: (ds: DocumentSummary) => boolean, k: K, v: DocumentSummary[K]) {
      this.summaries.filter(p).forEach((ds) => {
        Vue.set(ds, k, v);
      });
    },
    async generateSummary(selectionLabelIds: number[]) {
      try {
        this.generating = true;
        const additionalInstructions = this.additionalInstructions?.trim();
        const relatedText = this.relatedSummaries?.groups
          .filter((_, i) => this.includeRelated[i])
          .map((ds) => ds.head?.summary.text)
          .filter(notEmpty)
          .join('\n\n');

        const instructions = [
          ...(additionalInstructions ? [additionalInstructions] : []),
          ...(relatedText ? [this.contextInstructions, relatedText] : []),
        ].join('\n\n');

        const generated = await api.generateDocumentSummariesWithInstructions(
          this.documentId,
          { spec: { members: selectionLabelIds.map((lid) => [lid]) }, instructions },
        );
        const ds = generated[0];
        notify('Summary generated');
        /* eslint-disable no-undef */
        const group = this.group as Writeable<Group>;
        const tail = this.group?.tail ?? [];
        if (ds.head) {
          if (group.head) {
            group.tail = [...tail, group.head];
          }
          Vue.set(group, 'head', ds);
        } else {
          group.tail = [...generated, ...tail];
        }
        this.opened = ds.id;
        this.generating = false;
      } catch {
        notifyError('Could not generate summary');
      }
    },
    filterLabel(label: Label) {
      this.$emit('filterLabel', label);
    },
    diff(oldText: string, newText: string) {
      return diffText(oldText, newText);
    },
  },
});
